import { Box, Button, Loader, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { getRelativeDateTime } from '@wix/seo-common-core';
import { RefreshSmall } from '@wix/wix-ui-icons-common';
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { isScanningAtom, panelStateAtom, totalPagesScannedAtom } from '../../../atoms/session-atoms';
import { BiCtaNames } from '../../../bi/types';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useMultilingualWarning } from '../../../hooks/use-multilingual-warning';
import { useSessionInfoContext } from '../../../hooks/use-session-info-context';
import { PanoramaTransactions } from '../../../panorama/constants';
import { usePanoramaFunctions } from '../../../panorama/use-panorama-functions';
import { useAnalyzeFullSiteQuery } from '../../../queries/use-analyze-full-site-query';
import { dataHooks } from '../../../utils/datahooks';
import { isClassicEditorHost, isEditor3Host } from '../../../utils/get-editor-host';
export const WizardFooterDetectedIssues = _ref => {
  let {
    dataHook
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    startPanoramaTransaction,
    finishPanoramaTransaction
  } = usePanoramaFunctions();
  const biLogger = useBiLogger();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const {
    locale
  } = useSessionInfoContext();
  const {
    lastScanTimeStamp
  } = useAtomValue(panelStateAtom);
  const {
    shouldShowSecondaryLanguageWarning
  } = useMultilingualWarning();
  const relevantTimestamp = lastScanTimeStamp;
  const [lastScanTimeDisplay, setLastScanTimeDisplay] = useState(getRelativeDateTime(locale, relevantTimestamp));
  const {
    groupsDataMap
  } = useAtomValue(panelStateAtom);
  const totalPages = Array.from(groupsDataMap.values()).filter(group => !group.isTpaPage).length;
  const isScanning = useAtomValue(isScanningAtom);
  const totalPagesScanned = useAtomValue(totalPagesScannedAtom);
  const {
    refetch: analyzeFullSite,
    isFetching
  } = useAnalyzeFullSiteQuery();
  useEffect(() => {
    if (!relevantTimestamp) {
      return;
    }
    const intervalId = setInterval(() => {
      setLastScanTimeDisplay(getRelativeDateTime(locale, relevantTimestamp));
    }, 60 * 1_000);
    setLastScanTimeDisplay(getRelativeDateTime(locale, relevantTimestamp));
    return () => clearInterval(intervalId);
  }, [relevantTimestamp, locale]);
  const padding = isClassicEditorHost(editorHost) ? 'SP3 SP2 9px SP2' : 'SP2 0 SP2 0';
  const handleAnalyzeFullSiteClick = useCallback(async () => {
    startPanoramaTransaction(PanoramaTransactions.detectedIssueAnalyzeSite);
    biLogger.ctaClick({
      ctaName: BiCtaNames.ScanButton
    });
    await analyzeFullSite();
    finishPanoramaTransaction(PanoramaTransactions.detectedIssueAnalyzeSite);
  }, [analyzeFullSite, biLogger, finishPanoramaTransaction, startPanoramaTransaction]);
  return /*#__PURE__*/React.createElement(Box, {
    align: "center",
    dataHook: dataHooks.detectedIssuesFooter(dataHook),
    direction: "vertical",
    gap: "SP1",
    padding: padding,
    width: "252px"
  }, /*#__PURE__*/React.createElement(Button, {
    dataHook: dataHooks.detectedIssuesFooterScanAgainButton(dataHook),
    disabled: isScanning || isFetching || shouldShowSecondaryLanguageWarning,
    fullWidth: isEditor3Host(editorHost) ? false : true,
    prefixIcon: isClassicEditorHost(editorHost) ? /*#__PURE__*/React.createElement(RefreshSmall, null) : /*#__PURE__*/React.createElement(React.Fragment, null),
    priority: "secondary",
    suffixIcon: isScanning || isFetching ? /*#__PURE__*/React.createElement(Box, {
      margin: "-2px 0 0 4px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : undefined,
    onClick: handleAnalyzeFullSiteClick
  }, t(isScanning || isFetching ? 'accessibility-wizard.footer.results.scanning' : 'accessibility-wizard.footer.results.scan-again.cta')), /*#__PURE__*/React.createElement(Box, {
    dataHook: dataHooks.detectedIssueLastScanTime
  }, isScanning || isFetching ? /*#__PURE__*/React.createElement(Text, {
    size: "tiny"
  }, totalPagesScanned > totalPages ? totalPages : totalPagesScanned, "/", totalPages, ' ', t('accessibility-wizard.footer.results.pages-scanned')) : /*#__PURE__*/React.createElement(Text, {
    size: "tiny"
  }, t('accessibility-wizard.footer.results.scan-date.prefix'), ' ', lastScanTimeDisplay)));
};
//# sourceMappingURL=wizard-footer-detected-issues.js.map